import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { Download, AlertTriangle } from "lucide-react";

const COLORS = ["#D95D39", "#6A7F66", "#2A9D8F", "#E9C46A", "#F4A261", "#264653", "#E76F51"];

export default function MenuReport() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/reports/menu`, { params: { start_date: start, end_date: end } });
    setData(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportCSV = () => {
    if (!data?.top_items?.length) return;
    const rows = [["Item", "Category", "Qty Sold", "Revenue"]];
    data.top_items.forEach((i) => rows.push([i.name, i.category, i.qty, i.revenue]));
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `menu-report-${start}-to-${end}.csv`;
    a.click();
  };

  return (
    <div className="p-6 sm:p-8" data-testid="menu-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Menu Report</h1>
          <p className="text-sm text-[#7A7873] mt-1">Top sellers · slow movers · category mix · peak hours.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 border-[#EBE8E2]" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 border-[#EBE8E2]" /></div>
          <Button onClick={load} className="bg-[#D95D39] hover:bg-[#C24F2C] h-9" data-testid="run-menu-report">Run</Button>
          <Button variant="outline" onClick={exportCSV} className="h-9"><Download size={14} className="mr-1.5" />CSV</Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Items Sold</div>
              <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num">{data.totals.total_items_sold}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Unique Dishes Sold</div>
              <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num">{data.totals.unique_items_sold}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Revenue</div>
              <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num text-[#D95D39]">{inr(data.totals.total_revenue)}</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3">Top 10 Items by Revenue</h3>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={data.top_items.slice(0, 10)} layout="vertical" margin={{ left: 16 }}>
                    <CartesianGrid stroke="#EBE8E2" strokeDasharray="2 4" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#7A7873", fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#2D2C2A", fontSize: 11 }} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Bar dataKey="revenue" fill="#D95D39" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3">Revenue by Category</h3>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.by_category} dataKey="revenue" nameKey="name" outerRadius={110} label={(d) => d.name}>
                      {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card className="p-5 border-[#EBE8E2] bg-white mb-6">
            <h3 className="font-heading font-bold text-lg mb-3">Orders by Hour</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={data.by_hour}>
                  <CartesianGrid stroke="#EBE8E2" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: "#7A7873", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#7A7873", fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="orders" stroke="#6A7F66" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-[#E9C46A]" /> Slow Movers (sold least)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[400px]">
                  <thead className="text-[#7A7873] text-xs uppercase tracking-wider border-b border-[#EBE8E2]">
                    <tr>
                      <th className="text-left py-2">Item</th>
                      <th className="text-right py-2">Qty</th>
                      <th className="text-right py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bottom_items.map((i) => (
                      <tr key={i.name} className="border-b border-[#EBE8E2]">
                        <td className="py-2">{i.name}</td>
                        <td className="py-2 text-right font-mono-num">{i.qty}</td>
                        <td className="py-2 text-right font-mono-num">{inr(i.revenue)}</td>
                      </tr>
                    ))}
                    {data.bottom_items.length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-[#7A7873]">Not enough data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="p-5 border-[#EBE8E2] bg-white">
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-[#E76F51]" /> Never Sold (consider promoting or removing)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[400px]">
                  <thead className="text-[#7A7873] text-xs uppercase tracking-wider border-b border-[#EBE8E2]">
                    <tr>
                      <th className="text-left py-2">Item</th>
                      <th className="text-left py-2">Category</th>
                      <th className="text-right py-2">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.not_sold.map((i) => (
                      <tr key={i.name} className="border-b border-[#EBE8E2]">
                        <td className="py-2">{i.name}</td>
                        <td className="py-2 text-[#7A7873]">{i.category}</td>
                        <td className="py-2 text-right font-mono-num">{inr(i.price)}</td>
                      </tr>
                    ))}
                    {data.not_sold.length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-[#6A7F66]">Every active item sold at least once. </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
